import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Commit SHA baked into the bundle at build time by VITE_APP_VERSION.
// Undefined in local dev (env var not set) — hook exits early in that case.
const CURRENT_VERSION = import.meta.env.VITE_APP_VERSION as string | undefined;

/**
 * Polls /version.json every 5 minutes to detect when a new build has been
 * deployed to S3/CloudFront.
 *
 * The "current version" is the git commit SHA compiled into the JS bundle via
 * VITE_APP_VERSION. version.json on S3 is written with that same SHA during CI.
 * When the two differ, a new deploy has happened and the banner shows.
 *
 * This approach is race-condition-free: only the frontend CI job writes
 * version.json, using the same SHA that was baked into the bundle it just built.
 * CloudFront has a CachingDisabled behavior for /version.json so polls always
 * read the live S3 value.
 *
 * In local dev VITE_APP_VERSION is undefined, so the hook exits immediately —
 * the banner never appears locally.
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!CURRENT_VERSION) {
      console.log('[VersionCheck] VITE_APP_VERSION is not set — running in local dev mode, banner disabled.');
      return;
    }

    console.log(`[VersionCheck] Bundle version (VITE_APP_VERSION): ${CURRENT_VERSION}`);
    console.log(`[VersionCheck] Will poll /version.json every ${POLL_INTERVAL_MS / 1000}s.`);

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) {
          console.warn(`[VersionCheck] /version.json fetch failed: HTTP ${res.status}`);
          return;
        }
        const payload = await res.json();
        const { version } = payload;
        console.log(`[VersionCheck] /version.json → "${version}" | bundle → "${CURRENT_VERSION}" | match=${version === CURRENT_VERSION}`);
        if (version && version !== CURRENT_VERSION) {
          console.log('[VersionCheck] New version detected — setting updateAvailable=true (banner should appear).');
          setUpdateAvailable(true);
        }
      } catch (err) {
        console.warn('[VersionCheck] Fetch error (network issue during deploy?):', err);
      }
    };

    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return { updateAvailable };
}
